/**
 * Biomarker Catalog — Master reference for all biomarkers in the Hemologix.AI system.
 *
 * 130+ biomarkers across 15 panel categories with conventional ranges,
 * functional (optimal) ranges, and distribution parameters for synthetic
 * panel generation. This file is consumed by the Patient Journey Engine's
 * panel generator and does NOT replace the existing biomarkerRanges.ts.
 */

import type { CatalogBiomarker, BiomarkerPanelCategory } from '../../types/patientJourney';

export const BIOMARKER_CATALOG: CatalogBiomarker[] = [

  // ═══════════════════════════════════════════
  // CBC — Complete Blood Count (15 biomarkers)
  // ═══════════════════════════════════════════

  {
    id: 'wbc',
    name: 'WBC',
    unit: 'x10\u00B3/\u00B5L',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 4.5, high: 11.0 },
    functionalRange: { low: 5.0, high: 8.0 },
    xPrizeRange: { low: 5.0, high: 7.0 },
    distribution: { mean: 6.5, stdDev: 1.5, floor: 1.0, ceiling: 30.0 },
  },
  {
    id: 'rbc',
    name: 'RBC',
    unit: 'x10\u2076/\u00B5L',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 4.0, high: 5.5 },
    functionalRange: { low: 4.2, high: 4.9 },
    xPrizeRange: { low: 4.3, high: 4.7 },
    distribution: { mean: 4.5, stdDev: 0.35, floor: 2.0, ceiling: 7.5 },
  },
  {
    id: 'hemoglobin',
    name: 'Hemoglobin',
    unit: 'g/dL',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 12.0, high: 17.5 },
    functionalRange: { low: 13.5, high: 15.5 },
    xPrizeRange: { low: 14.0, high: 15.0 },
    distribution: { mean: 14.2, stdDev: 1.2, floor: 5.0, ceiling: 22.0 },
  },
  {
    id: 'hematocrit',
    name: 'Hematocrit',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 36, high: 52 },
    functionalRange: { low: 40, high: 48 },
    xPrizeRange: { low: 42, high: 46 },
    distribution: { mean: 43, stdDev: 3.5, floor: 15, ceiling: 65 },
  },
  {
    id: 'mcv',
    name: 'MCV',
    unit: 'fL',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 80, high: 100 },
    functionalRange: { low: 82, high: 95 },
    xPrizeRange: { low: 85, high: 92 },
    distribution: { mean: 89, stdDev: 4.5, floor: 50, ceiling: 130 },
  },
  {
    id: 'mch',
    name: 'MCH',
    unit: 'pg',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 27, high: 33 },
    functionalRange: { low: 28, high: 32 },
    xPrizeRange: { low: 29, high: 31 },
    distribution: { mean: 30, stdDev: 1.5, floor: 15, ceiling: 45 },
  },
  {
    id: 'mchc',
    name: 'MCHC',
    unit: 'g/dL',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 32, high: 36 },
    functionalRange: { low: 33, high: 35 },
    xPrizeRange: { low: 33.5, high: 34.5 },
    distribution: { mean: 34, stdDev: 1.0, floor: 25, ceiling: 40 },
  },
  {
    id: 'rdw',
    name: 'RDW',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 11.5, high: 14.5 },
    functionalRange: { low: 11.5, high: 13.0 },
    xPrizeRange: { low: 11.5, high: 12.5 },
    distribution: { mean: 12.5, stdDev: 0.7, skew: 0.8, floor: 10.0, ceiling: 25.0 },
  },
  {
    id: 'platelets',
    name: 'Platelets',
    unit: 'x10\u00B3/\u00B5L',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 150, high: 400 },
    functionalRange: { low: 200, high: 300 },
    xPrizeRange: { low: 210, high: 280 },
    distribution: { mean: 250, stdDev: 50, floor: 10, ceiling: 800 },
  },
  {
    id: 'mpv',
    name: 'MPV',
    unit: 'fL',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 7.5, high: 12.5 },
    functionalRange: { low: 8.0, high: 11.0 },
    xPrizeRange: { low: 8.5, high: 10.0 },
    distribution: { mean: 9.5, stdDev: 1.1, floor: 5.0, ceiling: 16.0 },
  },
  {
    id: 'neutrophils',
    name: 'Neutrophils',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 40, high: 70 },
    functionalRange: { low: 45, high: 65 },
    xPrizeRange: { low: 48, high: 60 },
    distribution: { mean: 55, stdDev: 7.0, floor: 5, ceiling: 95 },
  },
  {
    id: 'lymphocytes',
    name: 'Lymphocytes',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 20, high: 40 },
    functionalRange: { low: 25, high: 35 },
    xPrizeRange: { low: 27, high: 33 },
    distribution: { mean: 30, stdDev: 5.0, floor: 2, ceiling: 80 },
  },
  {
    id: 'monocytes',
    name: 'Monocytes',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 2, high: 8 },
    functionalRange: { low: 3, high: 7 },
    xPrizeRange: { low: 3, high: 6 },
    distribution: { mean: 5, stdDev: 1.5, floor: 0, ceiling: 20 },
  },
  {
    id: 'eosinophils',
    name: 'Eosinophils',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 1, high: 4 },
    functionalRange: { low: 1, high: 3 },
    xPrizeRange: { low: 1, high: 2 },
    distribution: { mean: 2, stdDev: 0.8, skew: 1.0, floor: 0, ceiling: 15 },
  },
  {
    id: 'basophils',
    name: 'Basophils',
    unit: '%',
    categories: ['cbc'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0, high: 1 },
    functionalRange: { low: 0, high: 0.5 },
    xPrizeRange: { low: 0, high: 0.3 },
    distribution: { mean: 0.3, stdDev: 0.2, skew: 1.5, floor: 0, ceiling: 5 },
  },

  // ═══════════════════════════════════════════════════════
  // CMP — Comprehensive Metabolic Panel (17 biomarkers)
  // ═══════════════════════════════════════════════════════

  {
    id: 'glucose',
    name: 'Glucose',
    unit: 'mg/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 65, high: 99 },
    functionalRange: { low: 70, high: 85 },
    xPrizeRange: { low: 72, high: 82 },
    distribution: { mean: 82, stdDev: 8, floor: 30, ceiling: 500 },
  },
  {
    id: 'bun',
    name: 'BUN',
    unit: 'mg/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 6, high: 20 },
    functionalRange: { low: 10, high: 16 },
    xPrizeRange: { low: 11, high: 15 },
    distribution: { mean: 13, stdDev: 3.5, floor: 2, ceiling: 100 },
  },
  {
    id: 'creatinine',
    name: 'Creatinine',
    unit: 'mg/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0.6, high: 1.2 },
    functionalRange: { low: 0.8, high: 1.1 },
    xPrizeRange: { low: 0.85, high: 1.05 },
    distribution: { mean: 0.95, stdDev: 0.15, floor: 0.2, ceiling: 15.0 },
  },
  {
    id: 'egfr',
    name: 'eGFR',
    unit: 'mL/min',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 60, high: 120 },
    functionalRange: { low: 90, high: 120 },
    xPrizeRange: { low: 95, high: 120 },
    distribution: { mean: 100, stdDev: 15, floor: 5, ceiling: 150 },
    ageBrackets: [
      { minAge: 20, maxAge: 39, conventionalRange: { low: 90, high: null }, functionalRange: { low: 100, high: null }, xPrizeRange: { low: 110, high: null } },
      { minAge: 40, maxAge: 59, conventionalRange: { low: 75, high: null }, functionalRange: { low: 90, high: null }, xPrizeRange: { low: 95, high: null } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 60, high: null }, functionalRange: { low: 80, high: null }, xPrizeRange: { low: 90, high: null } },
    ],
  },
  {
    id: 'sodium',
    name: 'Sodium',
    unit: 'mEq/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 136, high: 145 },
    functionalRange: { low: 138, high: 142 },
    xPrizeRange: { low: 139, high: 141 },
    distribution: { mean: 140, stdDev: 2.0, floor: 110, ceiling: 170 },
  },
  {
    id: 'potassium',
    name: 'Potassium',
    unit: 'mEq/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 3.5, high: 5.0 },
    functionalRange: { low: 4.0, high: 4.5 },
    xPrizeRange: { low: 4.0, high: 4.4 },
    distribution: { mean: 4.2, stdDev: 0.35, floor: 2.0, ceiling: 8.0 },
  },
  {
    id: 'chloride',
    name: 'Chloride',
    unit: 'mEq/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 98, high: 106 },
    functionalRange: { low: 100, high: 104 },
    xPrizeRange: { low: 101, high: 103 },
    distribution: { mean: 102, stdDev: 2.0, floor: 80, ceiling: 125 },
  },
  {
    id: 'co2',
    name: 'CO2',
    unit: 'mEq/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 23, high: 29 },
    functionalRange: { low: 24, high: 28 },
    xPrizeRange: { low: 25, high: 27 },
    distribution: { mean: 26, stdDev: 1.5, floor: 10, ceiling: 40 },
  },
  {
    id: 'calcium',
    name: 'Calcium',
    unit: 'mg/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 8.5, high: 10.5 },
    functionalRange: { low: 9.2, high: 10.0 },
    xPrizeRange: { low: 9.4, high: 9.8 },
    distribution: { mean: 9.5, stdDev: 0.4, floor: 5.0, ceiling: 15.0 },
  },
  {
    id: 'total_protein',
    name: 'Total Protein',
    unit: 'g/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 6.0, high: 8.3 },
    functionalRange: { low: 6.5, high: 7.5 },
    xPrizeRange: { low: 6.8, high: 7.2 },
    distribution: { mean: 7.0, stdDev: 0.5, floor: 3.0, ceiling: 12.0 },
  },
  {
    id: 'albumin',
    name: 'Albumin',
    unit: 'g/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 3.5, high: 5.5 },
    functionalRange: { low: 4.0, high: 5.0 },
    xPrizeRange: { low: 4.2, high: 4.8 },
    distribution: { mean: 4.5, stdDev: 0.35, floor: 1.5, ceiling: 6.5 },
  },
  {
    id: 'globulin',
    name: 'Globulin',
    unit: 'g/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 2.0, high: 3.5 },
    functionalRange: { low: 2.2, high: 2.8 },
    xPrizeRange: { low: 2.3, high: 2.6 },
    distribution: { mean: 2.5, stdDev: 0.35, floor: 1.0, ceiling: 6.0 },
  },
  {
    id: 'ag_ratio',
    name: 'A/G Ratio',
    unit: 'ratio',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 1.1, high: 2.5 },
    functionalRange: { low: 1.5, high: 2.0 },
    xPrizeRange: { low: 1.6, high: 1.9 },
    distribution: { mean: 1.7, stdDev: 0.25, floor: 0.3, ceiling: 4.0 },
  },
  {
    id: 'bun_creatinine_ratio',
    name: 'BUN/Creatinine Ratio',
    unit: 'ratio',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 6, high: 22 },
    functionalRange: { low: 10, high: 16 },
    xPrizeRange: { low: 10, high: 15 },
    distribution: { mean: 14, stdDev: 4, floor: 3, ceiling: 40 },
  },
  {
    id: 'bilirubin',
    name: 'Bilirubin',
    unit: 'mg/dL',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0.1, high: 1.2 },
    functionalRange: { low: 0.2, high: 0.8 },
    xPrizeRange: { low: 0.3, high: 0.7 },
    distribution: { mean: 0.5, stdDev: 0.25, skew: 1.2, floor: 0.0, ceiling: 20.0 },
  },
  {
    id: 'alp',
    name: 'ALP',
    unit: 'U/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 44, high: 147 },
    functionalRange: { low: 50, high: 100 },
    xPrizeRange: { low: 55, high: 85 },
    distribution: { mean: 70, stdDev: 20, skew: 0.8, floor: 10, ceiling: 500 },
  },
  {
    id: 'ast',
    name: 'AST',
    unit: 'U/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 10, high: 40 },
    functionalRange: { low: 10, high: 26 },
    xPrizeRange: { low: 10, high: 20 },
    distribution: { mean: 18, stdDev: 6, skew: 1.0, floor: 5, ceiling: 500 },
  },
  {
    id: 'alt',
    name: 'ALT',
    unit: 'U/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 7, high: 56 },
    functionalRange: { low: 10, high: 26 },
    xPrizeRange: { low: 10, high: 20 },
    distribution: { mean: 18, stdDev: 8, skew: 1.2, floor: 3, ceiling: 500 },
  },
  {
    id: 'ggt',
    name: 'GGT',
    unit: 'U/L',
    categories: ['cmp'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0, high: 65 },
    functionalRange: { low: 10, high: 30 },
    xPrizeRange: { low: 10, high: 20 },
    distribution: { mean: 18, stdDev: 12, skew: 2.0, floor: 0, ceiling: 500 },
  },

  // ══════════════════════════════════════════
  // Lipid Standard (5 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'total_cholesterol',
    name: 'Total Cholesterol',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 125, high: 200 },
    functionalRange: { low: 180, high: 220 },
    xPrizeRange: { low: 185, high: 210 },
    distribution: { mean: 195, stdDev: 30, floor: 80, ceiling: 400 },
  },
  {
    id: 'ldl',
    name: 'LDL',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0, high: 130 },
    functionalRange: { low: 70, high: 130 },
    xPrizeRange: { low: 70, high: 100 },
    distribution: { mean: 100, stdDev: 25, floor: 20, ceiling: 300 },
  },
  {
    id: 'hdl',
    name: 'HDL',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 40, high: 100 },
    functionalRange: { low: 60, high: 90 },
    xPrizeRange: { low: 65, high: 85 },
    distribution: { mean: 65, stdDev: 15, floor: 15, ceiling: 150 },
  },
  {
    id: 'triglycerides',
    name: 'Triglycerides',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0, high: 150 },
    functionalRange: { low: 50, high: 80 },
    xPrizeRange: { low: 50, high: 70 },
    distribution: { mean: 70, stdDev: 30, skew: 1.5, floor: 20, ceiling: 1000 },
  },
  {
    id: 'vldl',
    name: 'VLDL',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 5, high: 40 },
    functionalRange: { low: 5, high: 25 },
    xPrizeRange: { low: 5, high: 18 },
    distribution: { mean: 15, stdDev: 6, skew: 1.0, floor: 2, ceiling: 100 },
  },

  // Lipid Calculated / Ratios
  {
    id: 'tc_hdl_ratio',
    name: 'TC/HDL Ratio',
    unit: 'ratio',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 1.0, high: 5.0 },
    functionalRange: { low: 1.0, high: 3.5 },
    xPrizeRange: { low: 1.0, high: 3.0 },
    distribution: { mean: 3.5, stdDev: 1.0, floor: 1.0, ceiling: 10.0 },
  },
  {
    id: 'non_hdl_cholesterol',
    name: 'Non-HDL Cholesterol',
    unit: 'mg/dL',
    categories: ['lipid_standard'],
    tierMin: 2,
    gender: 'any',
    conventionalRange: { low: 0, high: 160 },
    functionalRange: { low: 0, high: 130 },
    xPrizeRange: { low: 0, high: 110 },
    distribution: { mean: 130, stdDev: 30, floor: 40, ceiling: 300 },
  },

  // ══════════════════════════════════════════
  // Lipid Advanced (7 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'apob',
    name: 'ApoB',
    unit: 'mg/dL',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 50, high: 130 },
    functionalRange: { low: 60, high: 90 },
    xPrizeRange: { low: 60, high: 80 },
    distribution: { mean: 75, stdDev: 18, floor: 20, ceiling: 200 },
  },
  {
    id: 'lpa',
    name: 'Lp(a)',
    unit: 'nmol/L',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 75 },
    functionalRange: { low: 0, high: 30 },
    xPrizeRange: { low: 0, high: 20 },
    distribution: { mean: 20, stdDev: 18, skew: 2.0, floor: 0, ceiling: 400 },
  },
  {
    id: 'ldl_particle_number',
    name: 'LDL Particle Number',
    unit: 'nmol/L',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 1300 },
    functionalRange: { low: 0, high: 1000 },
    xPrizeRange: { low: 0, high: 800 },
    distribution: { mean: 850, stdDev: 200, floor: 200, ceiling: 2500 },
  },
  {
    id: 'ldl_small',
    name: 'LDL Small',
    unit: 'nmol/L',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 527 },
    functionalRange: { low: 0, high: 200 },
    xPrizeRange: { low: 0, high: 120 },
    distribution: { mean: 150, stdDev: 100, skew: 1.2, floor: 0, ceiling: 1000 },
  },
  {
    id: 'oxidized_ldl',
    name: 'Oxidized LDL',
    unit: 'U/L',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 26, high: 117 },
    functionalRange: { low: 26, high: 60 },
    xPrizeRange: { low: 26, high: 45 },
    distribution: { mean: 45, stdDev: 15, skew: 0.8, floor: 10, ceiling: 200 },
  },
  {
    id: 'omega3_index',
    name: 'Omega-3 Index',
    unit: '%',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 4, high: 8 },
    functionalRange: { low: 8, high: 12 },
    xPrizeRange: { low: 9, high: 12 },
    distribution: { mean: 5.5, stdDev: 1.5, floor: 1, ceiling: 16 },
  },
  {
    id: 'omega6_omega3_ratio',
    name: 'Omega-6/Omega-3 Ratio',
    unit: 'ratio',
    categories: ['lipid_advanced'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 2, high: 10 },
    functionalRange: { low: 2, high: 4 },
    xPrizeRange: { low: 2, high: 3 },
    distribution: { mean: 5, stdDev: 2, skew: 1.0, floor: 1, ceiling: 25 },
  },

  // ══════════════════════════════════════════
  // Thyroid (6 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'tsh',
    name: 'TSH',
    unit: 'mIU/L',
    categories: ['thyroid', 'fertility'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 0.4, high: 4.5 },
    functionalRange: { low: 1.0, high: 2.0 },
    xPrizeRange: { low: 1.2, high: 1.6 },
    distribution: { mean: 1.8, stdDev: 0.8, skew: 1.5, floor: 0.01, ceiling: 100 },
  },
  {
    id: 'free_t3',
    name: 'Free T3',
    unit: 'pg/mL',
    categories: ['thyroid'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 2.0, high: 4.4 },
    functionalRange: { low: 3.0, high: 4.0 },
    xPrizeRange: { low: 3.3, high: 3.7 },
    distribution: { mean: 3.2, stdDev: 0.5, floor: 0.5, ceiling: 8.0 },
  },
  {
    id: 'free_t4',
    name: 'Free T4',
    unit: 'ng/dL',
    categories: ['thyroid'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 0.8, high: 1.8 },
    functionalRange: { low: 1.2, high: 1.5 },
    xPrizeRange: { low: 1.2, high: 1.4 },
    distribution: { mean: 1.3, stdDev: 0.2, floor: 0.2, ceiling: 5.0 },
  },
  {
    id: 'reverse_t3',
    name: 'Reverse T3',
    unit: 'ng/dL',
    categories: ['thyroid'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 9.2, high: 24.1 },
    functionalRange: { low: 9.2, high: 15.0 },
    xPrizeRange: { low: 10, high: 14 },
    distribution: { mean: 13, stdDev: 3.5, floor: 3, ceiling: 50 },
  },
  {
    id: 'tpo_antibodies',
    name: 'TPO Antibodies',
    unit: 'IU/mL',
    categories: ['thyroid', 'autoimmune'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 0, high: 34 },
    functionalRange: { low: 0, high: 10 },
    xPrizeRange: { low: 0, high: 4 },
    distribution: { mean: 8, stdDev: 8, skew: 3.0, floor: 0, ceiling: 1000 },
  },
  {
    id: 'thyroglobulin_antibodies',
    name: 'Thyroglobulin Antibodies',
    unit: 'IU/mL',
    categories: ['thyroid', 'autoimmune'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 40 },
    functionalRange: { low: 0, high: 4 },
    xPrizeRange: { low: 0, high: 2 },
    distribution: { mean: 3, stdDev: 5, skew: 4.0, floor: 0, ceiling: 500 },
  },

  // ═══════════════════════════════════════════════
  // Hormones — Women (9 biomarkers)
  // ═══════════════════════════════════════════════

  {
    id: 'estradiol_female',
    name: 'Estradiol',
    unit: 'pg/mL',
    categories: ['hormones_women', 'fertility'],
    tierMin: 1,
    gender: 'Female',
    conventionalRange: { low: 12.5, high: 166 },
    functionalRange: { low: 50, high: 100 },
    xPrizeRange: { low: 60, high: 90 },
    distribution: { mean: 75, stdDev: 30, skew: 0.8, floor: 5, ceiling: 500 },
  },
  {
    id: 'progesterone_female',
    name: 'Progesterone',
    unit: 'ng/mL',
    categories: ['hormones_women', 'fertility'],
    tierMin: 1,
    gender: 'Female',
    conventionalRange: { low: 1.8, high: 24 },
    functionalRange: { low: 10, high: 20 },
    xPrizeRange: { low: 12, high: 18 },
    distribution: { mean: 12, stdDev: 5, skew: 0.6, floor: 0.1, ceiling: 40 },
  },
  {
    id: 'fsh_female',
    name: 'FSH',
    unit: 'mIU/mL',
    categories: ['hormones_women', 'fertility'],
    tierMin: 1,
    gender: 'Female',
    conventionalRange: { low: 3.5, high: 12.5 },
    functionalRange: { low: 3.5, high: 8.0 },
    xPrizeRange: { low: 4.0, high: 7.0 },
    distribution: { mean: 6, stdDev: 2.0, skew: 0.6, floor: 0.5, ceiling: 150 },
  },
  {
    id: 'lh_female',
    name: 'LH',
    unit: 'mIU/mL',
    categories: ['hormones_women', 'fertility'],
    tierMin: 3,
    gender: 'Female',
    conventionalRange: { low: 2.4, high: 12.6 },
    functionalRange: { low: 3.0, high: 8.0 },
    xPrizeRange: { low: 3.5, high: 7.0 },
    distribution: { mean: 5, stdDev: 2.0, skew: 0.5, floor: 0.5, ceiling: 100 },
  },
  {
    id: 'dheas_female',
    name: 'DHEA-S',
    unit: '\u00B5g/dL',
    categories: ['hormones_women', 'fertility'],
    tierMin: 1,
    gender: 'Female',
    conventionalRange: { low: 35, high: 430 },
    functionalRange: { low: 150, high: 350 },
    xPrizeRange: { low: 180, high: 300 },
    distribution: { mean: 200, stdDev: 70, floor: 10, ceiling: 600 },
    ageBrackets: [
      { minAge: 20, maxAge: 29, conventionalRange: { low: 65, high: 380 }, functionalRange: { low: 250, high: 380 }, xPrizeRange: { low: 280, high: 380 } },
      { minAge: 30, maxAge: 39, conventionalRange: { low: 45, high: 320 }, functionalRange: { low: 200, high: 320 }, xPrizeRange: { low: 250, high: 350 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 32, high: 240 }, functionalRange: { low: 150, high: 250 }, xPrizeRange: { low: 200, high: 280 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 26, high: 200 }, functionalRange: { low: 120, high: 200 }, xPrizeRange: { low: 160, high: 230 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 20, high: 180 }, functionalRange: { low: 80, high: 160 }, xPrizeRange: { low: 120, high: 180 } },
    ],
  },
  {
    id: 'testosterone_total_female',
    name: 'Testosterone Total',
    unit: 'ng/dL',
    categories: ['hormones_women'],
    tierMin: 3,
    gender: 'Female',
    conventionalRange: { low: 8, high: 60 },
    functionalRange: { low: 20, high: 50 },
    xPrizeRange: { low: 25, high: 45 },
    distribution: { mean: 35, stdDev: 10, floor: 2, ceiling: 120 },
    ageBrackets: [
      { minAge: 20, maxAge: 29, conventionalRange: { low: 8, high: 60 }, functionalRange: { low: 30, high: 60 }, xPrizeRange: { low: 40, high: 60 } },
      { minAge: 30, maxAge: 39, conventionalRange: { low: 8, high: 55 }, functionalRange: { low: 30, high: 55 }, xPrizeRange: { low: 35, high: 55 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 8, high: 50 }, functionalRange: { low: 25, high: 50 }, xPrizeRange: { low: 30, high: 50 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 8, high: 45 }, functionalRange: { low: 20, high: 45 }, xPrizeRange: { low: 25, high: 45 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 8, high: 40 }, functionalRange: { low: 15, high: 40 }, xPrizeRange: { low: 20, high: 40 } },
    ],
  },
  {
    id: 'free_testosterone_female',
    name: 'Free Testosterone',
    unit: 'pg/mL',
    categories: ['hormones_women'],
    tierMin: 3,
    gender: 'Female',
    conventionalRange: { low: 0.3, high: 1.9 },
    functionalRange: { low: 0.5, high: 1.5 },
    xPrizeRange: { low: 0.7, high: 1.3 },
    distribution: { mean: 1.0, stdDev: 0.35, floor: 0.05, ceiling: 5.0 },
  },
  {
    id: 'shbg_female',
    name: 'SHBG',
    unit: 'nmol/L',
    categories: ['hormones_women'],
    tierMin: 3,
    gender: 'Female',
    conventionalRange: { low: 18, high: 144 },
    functionalRange: { low: 30, high: 80 },
    xPrizeRange: { low: 35, high: 65 },
    distribution: { mean: 50, stdDev: 22, skew: 0.8, floor: 5, ceiling: 300 },
  },
  {
    id: 'cortisol_am',
    name: 'Cortisol AM',
    unit: '\u00B5g/dL',
    categories: ['hormones_women', 'hormones_men'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 6.2, high: 19.4 },
    functionalRange: { low: 10, high: 15 },
    xPrizeRange: { low: 10, high: 14 },
    distribution: { mean: 12, stdDev: 3, floor: 1, ceiling: 40 },
  },

  // ═══════════════════════════════════════════════
  // Hormones — Men (9 biomarkers, cortisol shared above)
  // ═══════════════════════════════════════════════

  {
    id: 'testosterone_total_male',
    name: 'Testosterone Total',
    unit: 'ng/dL',
    categories: ['hormones_men'],
    tierMin: 1,
    gender: 'Male',
    conventionalRange: { low: 264, high: 916 },
    functionalRange: { low: 500, high: 900 },
    xPrizeRange: { low: 600, high: 850 },
    distribution: { mean: 600, stdDev: 130, floor: 50, ceiling: 1500 },
    ageBrackets: [
      { minAge: 20, maxAge: 29, conventionalRange: { low: 400, high: 916 }, functionalRange: { low: 600, high: 900 }, xPrizeRange: { low: 700, high: 950 } },
      { minAge: 30, maxAge: 39, conventionalRange: { low: 350, high: 850 }, functionalRange: { low: 550, high: 850 }, xPrizeRange: { low: 650, high: 900 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 300, high: 800 }, functionalRange: { low: 500, high: 800 }, xPrizeRange: { low: 600, high: 850 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 264, high: 750 }, functionalRange: { low: 450, high: 750 }, xPrizeRange: { low: 550, high: 800 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 264, high: 700 }, functionalRange: { low: 400, high: 700 }, xPrizeRange: { low: 500, high: 750 } },
    ],
  },
  {
    id: 'free_testosterone_male',
    name: 'Free Testosterone',
    unit: 'pg/mL',
    categories: ['hormones_men'],
    tierMin: 1,
    gender: 'Male',
    conventionalRange: { low: 8.7, high: 25.1 },
    functionalRange: { low: 15, high: 25 },
    xPrizeRange: { low: 17, high: 23 },
    distribution: { mean: 18, stdDev: 4, floor: 1, ceiling: 50 },
  },
  {
    id: 'shbg_male',
    name: 'SHBG',
    unit: 'nmol/L',
    categories: ['hormones_men'],
    tierMin: 3,
    gender: 'Male',
    conventionalRange: { low: 10, high: 57 },
    functionalRange: { low: 20, high: 40 },
    xPrizeRange: { low: 22, high: 35 },
    distribution: { mean: 28, stdDev: 10, floor: 3, ceiling: 120 },
  },
  {
    id: 'estradiol_male',
    name: 'Estradiol',
    unit: 'pg/mL',
    categories: ['hormones_men'],
    tierMin: 3,
    gender: 'Male',
    conventionalRange: { low: 8, high: 35 },
    functionalRange: { low: 15, high: 30 },
    xPrizeRange: { low: 18, high: 28 },
    distribution: { mean: 22, stdDev: 6, floor: 2, ceiling: 80 },
  },
  {
    id: 'fsh_male',
    name: 'FSH',
    unit: 'mIU/mL',
    categories: ['hormones_men'],
    tierMin: 3,
    gender: 'Male',
    conventionalRange: { low: 1.5, high: 12.4 },
    functionalRange: { low: 2.0, high: 6.0 },
    xPrizeRange: { low: 2.5, high: 5.0 },
    distribution: { mean: 4, stdDev: 2, skew: 0.8, floor: 0.3, ceiling: 40 },
  },
  {
    id: 'lh_male',
    name: 'LH',
    unit: 'mIU/mL',
    categories: ['hormones_men'],
    tierMin: 3,
    gender: 'Male',
    conventionalRange: { low: 1.7, high: 8.6 },
    functionalRange: { low: 3.0, high: 6.0 },
    xPrizeRange: { low: 3.5, high: 5.5 },
    distribution: { mean: 4.5, stdDev: 1.5, floor: 0.3, ceiling: 30 },
  },
  {
    id: 'dheas_male',
    name: 'DHEA-S',
    unit: '\u00B5g/dL',
    categories: ['hormones_men'],
    tierMin: 1,
    gender: 'Male',
    conventionalRange: { low: 80, high: 560 },
    functionalRange: { low: 200, high: 450 },
    xPrizeRange: { low: 250, high: 400 },
    distribution: { mean: 300, stdDev: 85, floor: 15, ceiling: 700 },
    ageBrackets: [
      { minAge: 20, maxAge: 29, conventionalRange: { low: 280, high: 640 }, functionalRange: { low: 400, high: 560 }, xPrizeRange: { low: 450, high: 560 } },
      { minAge: 30, maxAge: 39, conventionalRange: { low: 120, high: 520 }, functionalRange: { low: 350, high: 480 }, xPrizeRange: { low: 400, high: 500 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 95, high: 530 }, functionalRange: { low: 280, high: 430 }, xPrizeRange: { low: 350, high: 450 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 70, high: 310 }, functionalRange: { low: 200, high: 350 }, xPrizeRange: { low: 280, high: 380 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 42, high: 290 }, functionalRange: { low: 150, high: 290 }, xPrizeRange: { low: 200, high: 320 } },
    ],
  },
  {
    id: 'dht',
    name: 'DHT',
    unit: 'ng/dL',
    categories: ['hormones_men'],
    tierMin: 4,
    gender: 'Male',
    conventionalRange: { low: 30, high: 85 },
    functionalRange: { low: 35, high: 75 },
    xPrizeRange: { low: 40, high: 65 },
    distribution: { mean: 55, stdDev: 12, floor: 5, ceiling: 150 },
  },
  {
    id: 'prolactin_male',
    name: 'Prolactin',
    unit: 'ng/mL',
    categories: ['hormones_men'],
    tierMin: 4,
    gender: 'Male',
    conventionalRange: { low: 2, high: 18 },
    functionalRange: { low: 4, high: 12 },
    xPrizeRange: { low: 5, high: 10 },
    distribution: { mean: 8, stdDev: 3.5, skew: 1.0, floor: 0.5, ceiling: 100 },
  },

  // ═══════════════════════════════════════════════
  // Fertility (2 unique biomarkers, rest cross-categorized above)
  // ═══════════════════════════════════════════════

  {
    id: 'amh',
    name: 'AMH',
    unit: 'ng/mL',
    categories: ['fertility'],
    tierMin: 1,
    gender: 'Female',
    conventionalRange: { low: 1.0, high: 10.0 },
    functionalRange: { low: 2.0, high: 6.9 },
    xPrizeRange: { low: 2.5, high: 5.5 },
    distribution: { mean: 3.5, stdDev: 2.0, skew: 0.8, floor: 0.01, ceiling: 20 },
  },
  {
    id: 'prolactin_female',
    name: 'Prolactin',
    unit: 'ng/mL',
    categories: ['fertility'],
    tierMin: 3,
    gender: 'Female',
    conventionalRange: { low: 2, high: 29 },
    functionalRange: { low: 5, high: 20 },
    xPrizeRange: { low: 6, high: 16 },
    distribution: { mean: 12, stdDev: 5, skew: 0.8, floor: 0.5, ceiling: 200 },
  },

  // ══════════════════════════════════════════
  // Metabolic (7 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'fasting_glucose',
    name: 'Fasting Glucose',
    unit: 'mg/dL',
    categories: ['metabolic'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 65, high: 99 },
    functionalRange: { low: 70, high: 85 },
    xPrizeRange: { low: 72, high: 82 },
    distribution: { mean: 82, stdDev: 8, skew: 0.8, floor: 30, ceiling: 500 },
  },
  {
    id: 'hba1c',
    name: 'Hemoglobin A1c',
    unit: '%',
    categories: ['metabolic'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 4.0, high: 5.6 },
    functionalRange: { low: 4.5, high: 5.2 },
    xPrizeRange: { low: 4.6, high: 5.0 },
    distribution: { mean: 5.1, stdDev: 0.4, skew: 1.0, floor: 3.5, ceiling: 15.0 },
  },
  {
    id: 'fasting_insulin',
    name: 'Fasting Insulin',
    unit: '\u00B5IU/mL',
    categories: ['metabolic'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 2.6, high: 24.9 },
    functionalRange: { low: 2.0, high: 5.0 },
    xPrizeRange: { low: 2.0, high: 4.0 },
    distribution: { mean: 5.5, stdDev: 3.5, skew: 2.0, floor: 0.5, ceiling: 100 },
  },
  {
    id: 'homa_ir',
    name: 'HOMA-IR',
    unit: 'index',
    categories: ['metabolic'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 0, high: 2.5 },
    functionalRange: { low: 0, high: 1.0 },
    xPrizeRange: { low: 0, high: 0.7 },
    distribution: { mean: 1.2, stdDev: 0.8, skew: 2.0, floor: 0, ceiling: 25 },
  },
  {
    id: 'uric_acid',
    name: 'Uric Acid',
    unit: 'mg/dL',
    categories: ['metabolic'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 2.5, high: 7.0 },
    functionalRange: { low: 3.0, high: 5.5 },
    xPrizeRange: { low: 3.5, high: 5.0 },
    distribution: { mean: 4.5, stdDev: 1.0, floor: 1.0, ceiling: 15.0 },
  },
  {
    id: 'hscrp',
    name: 'hs-CRP',
    unit: 'mg/L',
    categories: ['metabolic', 'inflammation'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 0, high: 3.0 },
    functionalRange: { low: 0, high: 0.5 },
    xPrizeRange: { low: 0, high: 0.3 },
    distribution: { mean: 1.0, stdDev: 1.2, skew: 3.0, floor: 0, ceiling: 50 },
  },
  {
    id: 'homocysteine',
    name: 'Homocysteine',
    unit: '\u00B5mol/L',
    categories: ['metabolic', 'inflammation'],
    tierMin: 1,
    gender: 'any',
    conventionalRange: { low: 0, high: 15 },
    functionalRange: { low: 5, high: 8 },
    xPrizeRange: { low: 5, high: 7 },
    distribution: { mean: 8, stdDev: 3, skew: 1.5, floor: 2, ceiling: 50 },
  },

  // ══════════════════════════════════════════
  // Nutrients (10 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'vitamin_d',
    name: 'Vitamin D (25-OH)',
    unit: 'ng/mL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 30, high: 100 },
    functionalRange: { low: 50, high: 80 },
    xPrizeRange: { low: 55, high: 75 },
    distribution: { mean: 40, stdDev: 15, floor: 4, ceiling: 150 },
  },
  {
    id: 'b12',
    name: 'B12',
    unit: 'pg/mL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 200, high: 900 },
    functionalRange: { low: 500, high: 800 },
    xPrizeRange: { low: 550, high: 750 },
    distribution: { mean: 500, stdDev: 150, floor: 50, ceiling: 2000 },
  },
  {
    id: 'folate',
    name: 'Folate',
    unit: 'ng/mL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 2.7, high: 17.0 },
    functionalRange: { low: 10, high: 17 },
    xPrizeRange: { low: 12, high: 17 },
    distribution: { mean: 10, stdDev: 4, floor: 1, ceiling: 30 },
  },
  {
    id: 'iron',
    name: 'Iron',
    unit: '\u00B5g/dL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 60, high: 170 },
    functionalRange: { low: 85, high: 130 },
    xPrizeRange: { low: 90, high: 120 },
    distribution: { mean: 95, stdDev: 25, floor: 10, ceiling: 300 },
  },
  {
    id: 'ferritin',
    name: 'Ferritin',
    unit: 'ng/mL',
    categories: ['nutrients', 'inflammation'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 12, high: 150 },
    functionalRange: { low: 50, high: 100 },
    xPrizeRange: { low: 60, high: 90 },
    distribution: { mean: 70, stdDev: 35, skew: 1.5, floor: 2, ceiling: 1000 },
  },
  {
    id: 'magnesium_rbc',
    name: 'Magnesium RBC',
    unit: 'mg/dL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 4.2, high: 6.8 },
    functionalRange: { low: 5.5, high: 6.5 },
    xPrizeRange: { low: 5.8, high: 6.3 },
    distribution: { mean: 5.2, stdDev: 0.6, floor: 2.0, ceiling: 9.0 },
  },
  {
    id: 'zinc',
    name: 'Zinc',
    unit: '\u00B5g/dL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 60, high: 120 },
    functionalRange: { low: 80, high: 110 },
    xPrizeRange: { low: 85, high: 105 },
    distribution: { mean: 85, stdDev: 15, floor: 20, ceiling: 200 },
  },
  {
    id: 'tibc',
    name: 'TIBC',
    unit: '\u00B5g/dL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 250, high: 370 },
    functionalRange: { low: 280, high: 350 },
    xPrizeRange: { low: 290, high: 340 },
    distribution: { mean: 310, stdDev: 25, floor: 100, ceiling: 600 },
  },
  {
    id: 'iron_saturation',
    name: 'Iron Saturation',
    unit: '%',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 20, high: 50 },
    functionalRange: { low: 25, high: 40 },
    xPrizeRange: { low: 28, high: 37 },
    distribution: { mean: 30, stdDev: 7, floor: 2, ceiling: 90 },
  },
  {
    id: 'selenium',
    name: 'Selenium',
    unit: '\u00B5g/L',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 70, high: 150 },
    functionalRange: { low: 100, high: 140 },
    xPrizeRange: { low: 110, high: 135 },
    distribution: { mean: 115, stdDev: 18, floor: 20, ceiling: 250 },
  },
  {
    id: 'pth',
    name: 'PTH',
    unit: 'pg/mL',
    categories: ['nutrients'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 15, high: 65 },
    functionalRange: { low: 20, high: 45 },
    xPrizeRange: { low: 22, high: 38 },
    distribution: { mean: 35, stdDev: 12, skew: 1.0, floor: 5, ceiling: 300 },
  },

  // ══════════════════════════════════════════
  // Inflammation (4 unique; hsCRP, homocysteine, ferritin cross-listed above)
  // ══════════════════════════════════════════

  {
    id: 'esr',
    name: 'ESR',
    unit: 'mm/hr',
    categories: ['inflammation'],
    tierMin: 3,
    gender: 'any',
    conventionalRange: { low: 0, high: 20 },
    functionalRange: { low: 0, high: 10 },
    xPrizeRange: { low: 0, high: 5 },
    distribution: { mean: 8, stdDev: 6, skew: 2.0, floor: 0, ceiling: 120 },
  },
  {
    id: 'fibrinogen',
    name: 'Fibrinogen',
    unit: 'mg/dL',
    categories: ['inflammation'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 200, high: 400 },
    functionalRange: { low: 200, high: 300 },
    xPrizeRange: { low: 210, high: 270 },
    distribution: { mean: 250, stdDev: 45, floor: 50, ceiling: 800 },
  },
  {
    id: 'il6',
    name: 'IL-6',
    unit: 'pg/mL',
    categories: ['inflammation'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 7 },
    functionalRange: { low: 0, high: 2 },
    xPrizeRange: { low: 0, high: 1.2 },
    distribution: { mean: 1.2, stdDev: 1.5, skew: 3.5, floor: 0, ceiling: 100 },
  },
  {
    id: 'tnf_alpha',
    name: 'TNF-alpha',
    unit: 'pg/mL',
    categories: ['inflammation'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 8.1 },
    functionalRange: { low: 0, high: 3 },
    xPrizeRange: { low: 0, high: 1.5 },
    distribution: { mean: 1.5, stdDev: 1.5, skew: 3.0, floor: 0, ceiling: 50 },
  },

  // ══════════════════════════════════════════
  // Autoimmune (4 unique; TPO Ab & TgAb cross-listed above)
  // ══════════════════════════════════════════

  {
    id: 'ana',
    name: 'ANA',
    unit: 'titer',
    categories: ['autoimmune'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 1 },
    functionalRange: { low: 0, high: 0 },
    xPrizeRange: { low: 0, high: 0 },
    distribution: { mean: 0, stdDev: 0.15, skew: 5.0, floor: 0, ceiling: 1 },
  },
  {
    id: 'rheumatoid_factor',
    name: 'Rheumatoid Factor',
    unit: 'IU/mL',
    categories: ['autoimmune'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 14 },
    functionalRange: { low: 0, high: 5 },
    xPrizeRange: { low: 0, high: 3 },
    distribution: { mean: 3, stdDev: 3, skew: 3.0, floor: 0, ceiling: 200 },
  },
  {
    id: 'anti_ccp',
    name: 'Anti-CCP',
    unit: 'U/mL',
    categories: ['autoimmune'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 20 },
    functionalRange: { low: 0, high: 5 },
    xPrizeRange: { low: 0, high: 3 },
    distribution: { mean: 2, stdDev: 3, skew: 4.0, floor: 0, ceiling: 300 },
  },
  {
    id: 'ttg_iga',
    name: 'Celiac Panel tTG-IgA',
    unit: 'U/mL',
    categories: ['autoimmune'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 4 },
    functionalRange: { low: 0, high: 2 },
    xPrizeRange: { low: 0, high: 1 },
    distribution: { mean: 1, stdDev: 1, skew: 4.0, floor: 0, ceiling: 100 },
  },

  // ══════════════════════════════════════════
  // Heavy Metals (4 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'mercury',
    name: 'Mercury',
    unit: '\u00B5g/L',
    categories: ['heavy_metals'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 5 },
    functionalRange: { low: 0, high: 2 },
    xPrizeRange: { low: 0, high: 1 },
    distribution: { mean: 1, stdDev: 1.0, skew: 2.5, floor: 0, ceiling: 50 },
  },
  {
    id: 'lead',
    name: 'Lead',
    unit: '\u00B5g/dL',
    categories: ['heavy_metals'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 5 },
    functionalRange: { low: 0, high: 2 },
    xPrizeRange: { low: 0, high: 1 },
    distribution: { mean: 1.5, stdDev: 1.0, skew: 2.0, floor: 0, ceiling: 50 },
  },
  {
    id: 'arsenic',
    name: 'Arsenic',
    unit: '\u00B5g/L',
    categories: ['heavy_metals'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 35 },
    functionalRange: { low: 0, high: 10 },
    xPrizeRange: { low: 0, high: 5 },
    distribution: { mean: 5, stdDev: 5, skew: 2.5, floor: 0, ceiling: 200 },
  },
  {
    id: 'cadmium',
    name: 'Cadmium',
    unit: '\u00B5g/L',
    categories: ['heavy_metals'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 2 },
    functionalRange: { low: 0, high: 0.5 },
    xPrizeRange: { low: 0, high: 0.2 },
    distribution: { mean: 0.3, stdDev: 0.3, skew: 3.0, floor: 0, ceiling: 10 },
  },

  // ══════════════════════════════════════════
  // Cancer Markers (4 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'psa',
    name: 'PSA',
    unit: 'ng/mL',
    categories: ['cancer_markers'],
    tierMin: 4,
    gender: 'Male',
    conventionalRange: { low: 0, high: 4.0 },
    functionalRange: { low: 0, high: 1.5 },
    xPrizeRange: { low: 0, high: 0.8 },
    distribution: { mean: 0.8, stdDev: 0.6, skew: 2.5, floor: 0, ceiling: 100 },
    ageBrackets: [
      { minAge: 20, maxAge: 39, conventionalRange: { low: 0, high: 2.5 }, functionalRange: { low: 0, high: 1.5 }, xPrizeRange: { low: 0, high: 0.7 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 0, high: 2.5 }, functionalRange: { low: 0, high: 1.5 }, xPrizeRange: { low: 0, high: 0.7 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 0, high: 3.5 }, functionalRange: { low: 0, high: 2.0 }, xPrizeRange: { low: 0, high: 1.0 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 0, high: 4.0 }, functionalRange: { low: 0, high: 2.5 }, xPrizeRange: { low: 0, high: 1.5 } },
    ],
  },
  {
    id: 'ca125',
    name: 'CA-125',
    unit: 'U/mL',
    categories: ['cancer_markers'],
    tierMin: 4,
    gender: 'Female',
    conventionalRange: { low: 0, high: 35 },
    functionalRange: { low: 0, high: 20 },
    xPrizeRange: { low: 0, high: 12 },
    distribution: { mean: 12, stdDev: 8, skew: 2.0, floor: 0, ceiling: 500 },
  },
  {
    id: 'cea',
    name: 'CEA',
    unit: 'ng/mL',
    categories: ['cancer_markers'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 3.0 },
    functionalRange: { low: 0, high: 2.0 },
    xPrizeRange: { low: 0, high: 1.2 },
    distribution: { mean: 1.0, stdDev: 0.8, skew: 2.5, floor: 0, ceiling: 50 },
  },
  {
    id: 'afp',
    name: 'AFP',
    unit: 'ng/mL',
    categories: ['cancer_markers'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 10 },
    functionalRange: { low: 0, high: 5 },
    xPrizeRange: { low: 0, high: 3 },
    distribution: { mean: 2.5, stdDev: 2, skew: 3.0, floor: 0, ceiling: 500 },
  },

  // ══════════════════════════════════════════
  // Growth / Aging (4 biomarkers)
  // ══════════════════════════════════════════

  {
    id: 'igf1',
    name: 'IGF-1',
    unit: 'ng/mL',
    categories: ['growth_aging'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 100, high: 300 },
    functionalRange: { low: 150, high: 250 },
    xPrizeRange: { low: 170, high: 230 },
    distribution: { mean: 180, stdDev: 40, floor: 25, ceiling: 500 },
    ageBrackets: [
      { minAge: 20, maxAge: 29, conventionalRange: { low: 116, high: 358 }, functionalRange: { low: 150, high: 250 }, xPrizeRange: { low: 130, high: 200 } },
      { minAge: 30, maxAge: 39, conventionalRange: { low: 115, high: 307 }, functionalRange: { low: 130, high: 220 }, xPrizeRange: { low: 120, high: 180 } },
      { minAge: 40, maxAge: 49, conventionalRange: { low: 98, high: 282 }, functionalRange: { low: 110, high: 200 }, xPrizeRange: { low: 100, high: 170 } },
      { minAge: 50, maxAge: 59, conventionalRange: { low: 81, high: 238 }, functionalRange: { low: 100, high: 180 }, xPrizeRange: { low: 90, high: 150 } },
      { minAge: 60, maxAge: 120, conventionalRange: { low: 68, high: 226 }, functionalRange: { low: 80, high: 160 }, xPrizeRange: { low: 80, high: 140 } },
    ],
  },
  {
    id: 'gh',
    name: 'GH',
    unit: 'ng/mL',
    categories: ['growth_aging'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 10 },
    functionalRange: { low: 1, high: 5 },
    xPrizeRange: { low: 1.5, high: 4 },
    distribution: { mean: 2.5, stdDev: 2.0, skew: 2.0, floor: 0, ceiling: 40 },
  },
  {
    id: 'telomere_length',
    name: 'Telomere Length',
    unit: 'kb',
    categories: ['growth_aging'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 5, high: 15 },
    functionalRange: { low: 8, high: 12 },
    xPrizeRange: { low: 9, high: 12 },
    distribution: { mean: 9, stdDev: 2, floor: 2, ceiling: 20 },
  },
  {
    id: 'p_tau217',
    name: 'p-tau217',
    unit: 'pg/mL',
    categories: ['growth_aging'],
    tierMin: 4,
    gender: 'any',
    conventionalRange: { low: 0, high: 0.5 },
    functionalRange: { low: 0, high: 0.2 },
    xPrizeRange: { low: 0, high: 0.1 },
    distribution: { mean: 0.1, stdDev: 0.08, skew: 2.5, floor: 0, ceiling: 5 },
  },
];

// ═══════════════════════════════════════════════════════
// Helper Functions
// ═══════════════════════════════════════════════════════

const VERTICAL_CATEGORY_MAP: Record<string, BiomarkerPanelCategory[]> = {
  general_wellness: ['cbc', 'cmp', 'lipid_standard', 'metabolic'],
  thyroid: ['thyroid'],
  hormones_women: ['hormones_women', 'thyroid'],
  hormones_men: ['hormones_men'],
  fertility: ['fertility', 'thyroid', 'hormones_women'],
  metabolic: ['metabolic', 'cmp', 'lipid_standard'],
  cardiovascular: ['lipid_standard', 'lipid_advanced', 'inflammation', 'metabolic'],
  longevity: ['lipid_advanced', 'inflammation', 'growth_aging', 'nutrients', 'metabolic'],
  autoimmune: ['autoimmune', 'thyroid', 'inflammation'],
  gut_health: ['autoimmune', 'nutrients', 'inflammation'],
  weight_management: ['metabolic', 'thyroid', 'hormones_women', 'hormones_men'],
  athletic_performance: ['cbc', 'nutrients', 'hormones_men', 'inflammation', 'metabolic'],
  mens_health: ['hormones_men', 'metabolic', 'lipid_standard', 'cancer_markers'],
  womens_health: ['hormones_women', 'fertility', 'thyroid', 'nutrients', 'cancer_markers'],
  environmental_toxins: ['heavy_metals'],
  cancer_screening: ['cancer_markers'],
};

export function getBiomarkersForTier(tier: 1 | 2 | 3 | 4): CatalogBiomarker[] {
  return BIOMARKER_CATALOG.filter((b) => b.tierMin <= tier);
}

export function getBiomarkersForCategory(category: BiomarkerPanelCategory): CatalogBiomarker[] {
  return BIOMARKER_CATALOG.filter((b) => b.categories.includes(category));
}

export function getBiomarkersForVertical(vertical: string): CatalogBiomarker[] {
  const categories = VERTICAL_CATEGORY_MAP[vertical];
  if (!categories) return [];
  return BIOMARKER_CATALOG.filter((b) =>
    b.categories.some((cat) => categories.includes(cat)),
  );
}

export function getBiomarkerById(id: string): CatalogBiomarker | undefined {
  return BIOMARKER_CATALOG.find((b) => b.id === id);
}
